import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ReactNode, useState } from "react";
import ProviderUserModal from "../../../../components/app/admin/provider-groups/ProviderUserDrawer";
import CustomTitle from "../../../../components/app/CustomTitle";
import ProviderProfileDrawer from "../../../../components/app/ProviderProfileDrawer";
import CustomCard from "../../../../components/app/settings/user-settings/CustomCard";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import CustomSelect from "../../../../components/CustomSelect";
import Flex from "../../../../components/Flex";
import { ScrollArea } from "../../../../components/ScrollBar";
import { STATUS } from "../../../../constants/AutocompleteOptions";
import { PROVIDER_USER_EDIT_INITIAL_VALUES } from "../../../../constants/ProviderUserSchema";
import UserData from "../../../../dummy-data/usersData";
import useCustomPagination from "../../../../hooks/use-custom-pagination";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useUsersList } from "../../../../queries/use-get-users";
import ProviderUserTable from "./ProviderUserTable";
import { UserCardData } from "../../../../types/user-types";

export type CurView = "card_view" | "table_view";

const TenantProviders = () => {
  const USERS_PER_PAGE = 8;

  const [
    editProviderUserModalDisclosure,
    addProviderUserModalDisclosure,
    providerProfileModalDisclosure,
  ] = useReduceDisclosure(3);

  const [pagination, setPagination] = useCustomPagination({
    totalRecords: UserData.length,
    recordsPerPage: USERS_PER_PAGE,
  });
  const { data: providersList } = useUsersList({
    pagination: {
      page: pagination.currentPage,
      pageSize: pagination.recordsPerPage,
    },
  });
  const [selectedUserData, setSelectedUserData] = useState<UserCardData | null>(
    null
  );

  const [userStatus, setUserStatus] = useState<boolean>(false);

  const [curView, setCurView] = useState<CurView>("card_view");

  function handleChange(_: React.ChangeEvent<unknown>, pageNumber: number) {
    setPagination((prevPage) => ({
      ...prevPage,
      currentPage: pageNumber - 1,
    }));
  }

  function handleStatusChange(userId: string, status: boolean): Promise<any> {
    const user = UserData.find((user) => user.id === userId);
    if (user) {
      user.active = status;
      setUserStatus(!userStatus);
      return new Promise(() => {});
    }
    return new Promise(() => {});
  }

  function handleOpenEditProfileModal(user?: UserCardData) {
    if (user) setSelectedUserData(user);
    editProviderUserModalDisclosure.open();
  }

  const ACTIVE_VIEW_MAPPER: Record<CurView, ReactNode> = {
    card_view: (
      <Grid container gap={1} justifyContent="center">
        {providersList?.data?.content?.map((user) => {
          return (
            <Grid item key={user?.value}>
              <CustomCard
                userData={user}
                handleOpenEditModal={() =>
                  handleOpenEditProfileModal(user as any)
                }
                handleOpenProfileModal={() => {
                  setSelectedUserData(user as any);
                  providerProfileModalDisclosure.open();
                }}
                onStatusChange={() =>
                  handleStatusChange(user?.id, user?.active)
                }
              />
            </Grid>
          );
        })}
      </Grid>
    ),
    table_view: <ProviderUserTable />,
  };

  return (
    <Box>
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <CustomTitle variant="h3">Tenant Provider</CustomTitle>
        <Flex gap={2} alignItems={"center"}>
          <TextField
            placeholder="Search"
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box width={"35%"}>
            <CustomSelect options={STATUS as any} placeholder="Status" />
          </Box>

          {/* TODO need to build custom component for multiple user and reduce snippets */}
          <ToggleButtonGroup
            value={curView}
            exclusive
            onChange={(_, v) => v && setCurView(v)}
            sx={{
              height: "38px",
              "& .css-ueukts-MuiButtonBase-root-MuiToggleButton-root.Mui-selected":
                {
                  backgroundColor: "#004186",
                  color: "#fff",
                },
              "&  .css-ueukts-MuiButtonBase-root-MuiToggleButton-root.Mui-selected:hover":
                {
                  backgroundColor: "#004186",
                },
            }}
          >
            <ToggleButton value="table_view">
              <ViewListOutlinedIcon />
            </ToggleButton>
            <ToggleButton value="card_view">
              <GridViewOutlinedIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <StyledButtonV2
            label="Add Tenant Provider"
            sx={{ width: "20rem" }}
            onClick={addProviderUserModalDisclosure.open}
            shouldAddIcon
          />
        </Flex>
      </Flex>

      <ScrollArea marginTop={"10px"} height="calc(100vh - 235px)">
        {ACTIVE_VIEW_MAPPER[curView]}
      </ScrollArea>
      {curView === "card_view" && (
        <Flex
          justifyContent={{
            sm: "center",
            md: "flex-end",
          }}
          position={"fixed"}
          bottom={"30px"}
          right={"18px"}
          width={"96%"}
          bgcolor={"white"}
          zIndex={"1000"}
          padding={"0px 5px 5px 0px"}
          alignItems={"baseline"}
        >
          <Pagination
            count={providersList?.data?.page?.totalPages}
            page={pagination.currentPage + 1}
            onChange={handleChange}
            color="primary"
            shape="rounded"
          />
        </Flex>
      )}

      <ProviderProfileDrawer
        disClosureHost={providerProfileModalDisclosure}
        providerUuid={selectedUserData?.uuid}
      />

      <ProviderUserModal
        open={addProviderUserModalDisclosure.opened}
        title={"Add Provider User"}
        onClose={addProviderUserModalDisclosure.close}
        onSubmit={editProviderUser}
        isAdminAccessField
      />

      <ProviderUserModal
        open={editProviderUserModalDisclosure.opened}
        title={"Edit Provider User"}
        onClose={editProviderUserModalDisclosure.close}
        onSubmit={editProviderUser}
        // todo need to change prefill values as per selected user data
        prefillValues={PROVIDER_USER_EDIT_INITIAL_VALUES ?? selectedUserData}
        isAdminAccessField
      />
    </Box>
  );
};

export default TenantProviders;

function editProviderUser() {
  return new Promise(() => {});
  // editProviderUserModalDisclosure.close();
}
